<ng-container *ngIf="initialized; else variablesLoadingSkeleton">

    <p-contextMenu #variableContextMenu [model]="contextMenuItems"></p-contextMenu>

    <div class="flex flex-column h-full">

        <div class="flex justify-content-between align-items-center mb-2 px-1">
            <span class="font-semibold text-sm">Variablen</span>
            <div class="flex gap-1" *ngIf="isAddButtonShown">
                <p-button icon="fa-regular fa-plus mr-0" label="Einfache Variable"
                          styleClass="p-button-text p-button-sm surface-50" (click)="handleSimpleVariable()"></p-button>
                <p-button icon="fa-regular fa-plus mr-0" label="Objektvariable"
                          styleClass="p-button-text p-button-sm surface-50" (click)="handleObjectVariable()"></p-button>
            </div>
        </div>

        <!-- Custom search input for deep search across lazy-loaded metadata -->
        <div class="mb-2 px-1">
            <div class="flex align-items-center gap-1">
                <span class="p-input-icon-left flex-grow-1">
                    <i class="fa-regular fa-magnifying-glass"></i>
                    <input type="text" pInputText class="w-full text-sm" placeholder="Variable suchen..."
                           [(ngModel)]="searchTerm" (input)="onSearchInput($event)" />
                </span>
                <i *ngIf="searchTerm" class="fa-regular fa-xmark cursor-pointer text-color-secondary hover:text-color text-sm"
                   (click)="clearSearch()"></i>
            </div>
        </div>

        <div *ngIf="searchTerm && searchResultTree.length === 0 && !busy" class="px-2 py-3 text-sm text-color-secondary">
            Keine Ergebnisse gefunden.
        </div>

        <p-tree
                *ngIf="!searchTerm || searchResultTree.length > 0 || busy"
                [value]="searchTerm && searchResultTree.length > 0 ? searchResultTree : variables"
                [selectionMode]="selectMode"
                [style]="{ width: treeWidth ? '185px' : '' }"
                (onNodeExpand)="nodeExpand($event)"
                (onNodeSelect)="nodeSelect($event)"
                [loading]="busy"
                styleClass="p-fluid text-sm pl-0 pr-2 template-component-treenode border-0"
                class="scrollable-section"
                [(selection)]="selectionNode"
        >
            <ng-template pTemplate="togglericon" let-expanded>
                <span class="te-tree-toggler">{{ expanded ? '▼' : '▶' }}</span>
            </ng-template>

            <!-- System root nodes (no action buttons) -->
            <ng-template let-node pTemplate="system-root">
                <div class="te-tree-row w-full">
                    <span class="te-tree-label">{{ node.label }}</span>
                    <span class="te-tree-alias text-color-secondary text-xs ml-auto">{{ node.data.alias }}</span>
                </div>
            </ng-template>

            <!-- Object root nodes (edit/delete via context menu only) -->
            <ng-template let-node pTemplate="object-root">
                <div class="te-tree-row w-full"
                     (contextmenu)="handleContextMenu($event, node); variableContextMenu.show($event)">
                    <span class="te-tree-label">{{ node.label }}</span>
                </div>
            </ng-template>

            <!-- Simple variable root nodes (insert/copy + edit/delete via context menu) -->
            <ng-template let-node pTemplate="simple-root">
                <div class="te-tree-row w-full"
                     (contextmenu)="handleContextMenu($event, node); variableContextMenu.show($event)">
                    <span class="te-tree-label">{{ node.label }}</span>
                    <span class="te-node-actions">
                        <p-button icon="fa-regular fa-plus" [text]="true"
                                  styleClass="p-button-sm te-action-btn te-action-insert" pTooltip="Variable einfügen" tooltipPosition="top"
                                  aria-label="Variable einfügen"
                                  (click)="handleInsertVariable(node); $event.stopPropagation()"></p-button>
                        <p-button icon="fa-regular fa-clipboard" [text]="true"
                                  styleClass="p-button-sm te-action-btn" pTooltip="Kopieren" tooltipPosition="top"
                                  aria-label="Pfad kopieren"
                                  (click)="handleCopyPopover(node, $event); $event.stopPropagation()"></p-button>
                    </span>
                </div>
            </ng-template>

            <!-- Attribute leaf nodes -->
            <ng-template let-node pTemplate="attribute">
                <div class="te-tree-row w-full" [class.te-search-match]="node.data?.matched">
                    <span class="te-tree-label">{{ node.label }}
                        @if (getNodeBreadcrumb(node).length > 0) {
                            <br/><span class="text-xs text-500">{{ getNodeBreadcrumb(node).join(' \u2192 ') }}</span>
                        }</span>
                    <span class="te-node-actions">
                        <p-button icon="fa-regular fa-plus" [text]="true"
                                  styleClass="p-button-sm te-action-btn te-action-insert" pTooltip="Variable einfügen" tooltipPosition="top"
                                  aria-label="Variable einfügen"
                                  (click)="handleInsertVariable(node); $event.stopPropagation()"></p-button>
                        <p-button label="IF" [text]="true" styleClass="p-button-sm te-action-btn te-action-if"
                                  pTooltip="IF-Bedingung" tooltipPosition="top" aria-label="IF-Bedingung einfügen"
                                  (click)="handleInsertIf(node); $event.stopPropagation()"></p-button>
                        <p-button icon="fa-regular fa-clipboard" [text]="true"
                                  styleClass="p-button-sm te-action-btn" pTooltip="Kopieren" tooltipPosition="top"
                                  aria-label="Pfad kopieren"
                                  (click)="handleCopyPopover(node, $event); $event.stopPropagation()"></p-button>
                    </span>
                </div>
            </ng-template>

            <!-- Collection/OneToMany nodes -->
            <ng-template let-node pTemplate="collection">
                <div class="te-tree-row w-full" [class.te-search-match]="node.data?.matched">
                    @if (node.data?.searchExpanded) {
                        <span class="te-search-toggler" pTooltip="Alle Attribute anzeigen" tooltipPosition="top"
                              (click)="expandNodeFully(node); $event.stopPropagation()"><i class="fa-regular fa-filter"></i></span>
                    }
                    <span class="te-tree-label">{{ node.label }}
                        @if (getNodeBreadcrumb(node).length > 0) {
                            <br/><span class="text-xs text-500">{{ getNodeBreadcrumb(node).join(' \u2192 ') }}</span>
                        }</span>
                    <span class="te-node-actions">
                        <p-button label="LOOP" [text]="true" styleClass="p-button-sm te-action-btn te-action-loop"
                                  pTooltip="Loop einfügen" tooltipPosition="top" aria-label="Loop einfügen"
                                  (click)="handleInsertLoop(node); $event.stopPropagation()"></p-button>
                        <p-button label="TABLE" [text]="true" styleClass="p-button-sm te-action-btn te-action-table"
                                  pTooltip="Tabelle erstellen" tooltipPosition="top" aria-label="Tabelle erstellen"
                                  (click)="handleInsertTable(node); $event.stopPropagation()"></p-button>
                        <p-button icon="fa-regular fa-clipboard" [text]="true"
                                  styleClass="p-button-sm te-action-btn" pTooltip="Kopieren" tooltipPosition="top"
                                  aria-label="Pfad kopieren"
                                  (click)="handleCopyPopover(node, $event); $event.stopPropagation()"></p-button>
                    </span>
                </div>
            </ng-template>

            <!-- Join nodes (ManyToOne, OneToOne) -->
            <ng-template let-node pTemplate="join">
                <div class="te-tree-row w-full" [class.te-search-match]="node.data?.matched">
                    @if (node.data?.searchExpanded) {
                        <span class="te-search-toggler" pTooltip="Alle Attribute anzeigen" tooltipPosition="top"
                              (click)="expandNodeFully(node); $event.stopPropagation()"><i class="fa-regular fa-filter"></i></span>
                    }
                    <span class="te-tree-label">{{ node.label }}
                        @if (getNodeBreadcrumb(node).length > 0) {
                            <br/><span class="text-xs text-500">{{ getNodeBreadcrumb(node).join(' \u2192 ') }}</span>
                        }</span>
                    <span class="te-node-actions">
                        <p-button icon="fa-regular fa-clipboard" [text]="true"
                                  styleClass="p-button-sm te-action-btn" pTooltip="Kopieren" tooltipPosition="top"
                                  aria-label="Pfad kopieren"
                                  (click)="handleCopyPopover(node, $event); $event.stopPropagation()"></p-button>
                    </span>
                </div>
            </ng-template>

        </p-tree>

    </div>
    <mvs-te-copy-popover #copyPopover></mvs-te-copy-popover>
</ng-container>

<ng-template #variablesLoadingSkeleton>
    <div class="px-1">
        <div class="flex justify-content-between align-items-center mb-3">
            <p-skeleton width="5rem" height="1rem"></p-skeleton>
            <div class="flex gap-1">
                <p-skeleton width="2rem" height="2rem" borderRadius="50%"></p-skeleton>
                <p-skeleton width="2rem" height="2rem" borderRadius="50%"></p-skeleton>
            </div>
        </div>
        <p-skeleton width="100%" height="2rem" styleClass="mb-3"></p-skeleton>
        <div class="flex align-items-center gap-2 mb-2" *ngFor="let i of [1,2,3,4,5,6]">
            <p-skeleton width="1.5rem" height="1.25rem" borderRadius="4px"></p-skeleton>
            <p-skeleton [width]="(70 + i * 5) + '%'" height="1.25rem"></p-skeleton>
        </div>
    </div>
</ng-template>

<p-dialog header="Variable anlegen" (onHide)="handleVariableDialogHide()" [(visible)]="visible" [modal]="true" [style]="{width: '50vw'}">

    <ng-container *ngIf="variableToggle">
        <div class="grid text-sm">
            <div class="col-12">
                <span>Alias (Technischer Name)</span>
                <br/>
                <input type="text" pInputText [(ngModel)]="variableAlias"/>
            </div>
            <div class="col-12">
                <span>Name der Variable</span>
                <br/>
                <input type="text" pInputText [(ngModel)]="variableName"/>
            </div>
            <div class="col-12">
                <span>Variablentyp:</span>
                <br/>
                <p-select
                        [options]="dataTypes"
                        [(ngModel)]="selectedVariableType"
                        optionLabel="name"
                        placeholder="Typ auswählen"
                        appendTo="body"
                ></p-select>
            </div>

            <div class="col-12">
                <div class="flex gap-3 justify-content-end">
                    <p-button label="Abbrechen" class="p-button-text" (click)="onCancel()"></p-button>
                    <p-button
                            *ngIf="variableName && selectedVariableType"
                            label="Speichern"
                            severity="primary"
                            [disabled]="!variableName || !selectedVariableType"
                            (click)="createVariable(teVariableTypeEnum.simple, selectedVariableType)"
                    ></p-button>
                </div>
            </div>

        </div>
    </ng-container>

    <ng-container *ngIf="!variableToggle">
        <div class="grid text-sm">
            <div class="col-12">
                <span>Alias (Technischer Name)</span>
                <br/>
                <input type="text" pInputText [(ngModel)]="variableAlias"/>
            </div>
            <div class="col-12">
                <span>Name der Variable:</span>
                <br/>
                <input
                        type="text"
                        class="w-12"
                        pInputText
                        [(ngModel)]="variableName"
                />
            </div>
            <div class="col-12">
                <span>Objekttyp:</span>
                <br/>
                <p-select
                        [options]="objectTypes"
                        [(ngModel)]="selectedObjectType"
                        [filter]="true"
                        optionLabel="displayLabel"
                        filterBy="displayLabel,alias"
                        appendTo="body"
                ></p-select>
            </div>
            <div class="col-12">
                <div class="flex gap-3 justify-content-end">
                    <p-button label="Abbrechen" class="p-button-text" (click)="onCancel()"></p-button>
                    <p-button
                            *ngIf="variableName && selectedObjectType"
                            label="Speichern"
                            severity="primary"
                            [disabled]="!variableName || !selectedObjectType"
                            (click)="createVariable(teVariableTypeEnum.object,selectedObjectType)"
                    ></p-button>
                </div>
            </div>

        </div>
    </ng-container>

</p-dialog>